// kernel/kernelStores.ts
import { createStore, useStore, type StoreApi } from 'zustand';
import { createContext, useContext } from 'react';
import { RgbaColor } from 'react-colorful';
import { modelDefaultParamGetters } from '#lib/models';
import { DEFAULT_STUDIO_MODEL_ID } from '#lib/model-registry';
import { defaultShortcuts } from '#lib/utils';
import { LEGACY_COMPAT_USER_ACTIVITIES } from '#lib/user-activities';
import { LIGHT_DEFAULTS } from '#lib/modelist/light';
import { RENDER_DEFAULTS } from '#lib/modelist/render';
import {
  isRecord,
  mergeEntityWithDraft,
  KERNEL_ENTITY_TO_INVENTORY_ENTITY,
} from '@/kernel/entityMerge';

import type {
  AgentMode,
  AssistantTurnDebugInfo,
  CloudAsset,
  CreditsData,
  DiffusionParams,
  InpaintTool,
  MaskPenMode,
  MaskShape,
  SoundTimelineTool,
  KernelState,
  SourcingMandate,
  LeasingAgentRoleId,
  MarketSignalStats,
  LockableFile,
  ModalName,
  SaveTelemetry,
  QuickMenuData,
  SocketStatus,
  ThemeOptions,
  UndoableAction,
  UserAction,
  UserContext,
  UserPreferences,
  KernelSetters,
  AgentObservedError,
  InventoryEntityType,
  LeasingActionItem,
  EntityKernelStateRegistry,
  KernelInventoryState,
  KernelEntityType,
  InventoryCountState,
  InventoryStatsState,
  TimelineFeedIndex,
  TimelineFeedStateRegistry,
  StudioViewMode,
  StudioSection,
  ModelistBudgetChoice,
  Fater3DState,
} from '#types/types';

/* =========================================================================
 * Inventory read model, canonical DTO + local draft + runtime overlay
 * The store keeps canonical inventory (server DTOs) separate from per-entity
 * local drafts/runtime overlays (`entityKernelState`); a read fuses them into
 * one coherent entity. The fusion is memoized per slice so an unrelated draft
 * write (e.g. a canvas pan touching a Project draft) keeps the StudioLayer /
 * StudioWorkspace slice references stable, otherwise every subscriber of those
 * slices re-renders on each pan. The reuse guard lives in withMergedInventory.
 * ========================================================================= */

// Draft flag marking an entity as locally deleted (dropped from the fused registry).
const LOCAL_ENTITY_DELETE_MARKER = '_isDeletedLocally';

const resolveInventoryEntityType = (entityType: KernelEntityType): InventoryEntityType | null =>
  KERNEL_ENTITY_TO_INVENTORY_ENTITY[entityType];

// Per-slice memo keyed by the canonical slice reference, naturally per-store and GC'd when the
// slice is replaced. Holds the draft refs that produced the last fused slice, so we can reuse it.
type MergedSliceMemo = { draftRefs: Map<string, unknown>; slice: unknown };
const mergedSliceCache = new WeakMap<object, MergedSliceMemo>();

// Fuse canonical inventory with local drafts/runtime overlays. Rebuilds only the slices whose
// canonical data or drafts actually moved; every untouched slice keeps its previous reference,
// so a write to one entity type never re-renders subscribers of the others (the repaired guard).
export const withMergedInventory = (state: KernelState): KernelInventoryState => {
  // Bucket each runtime draft under its target slice; skip drafts with no slice mapping or bad shape.
  const draftsByType = new Map<InventoryEntityType, Array<{ id: string; draft: Record<string, unknown> }>>();
  for (const entry of Object.values(state.entityKernelState)) {
    const type = resolveInventoryEntityType(entry.entityType);
    if (!type || !isRecord(entry.state)) continue;
    const bucket = draftsByType.get(type);
    if (bucket) bucket.push({ id: entry.entityId, draft: entry.state });
    else draftsByType.set(type, [{ id: entry.entityId, draft: entry.state }]);
  }
  if (draftsByType.size === 0) return state.inventory;

  const merged = { ...state.inventory } as KernelInventoryState;

  for (const [type, drafts] of draftsByType) {
    const canonicalSlice = (state.inventory as any)[type] as { registry?: Record<string, unknown> } | undefined;
    if (!canonicalSlice) continue;

    // Guard: reuse the last fused slice by reference when its canonical ref and every draft ref match.
    const cached = mergedSliceCache.get(canonicalSlice);
    const reusable =
      !!cached &&
      cached.draftRefs.size === drafts.length &&
      drafts.every(({ id, draft }) => cached.draftRefs.get(id) === draft);
    if (reusable) {
      (merged as any)[type] = cached!.slice;
      continue;
    }

    // Rebuild: start from canonical rows, then overlay runtime onto each drafted row (or drop deleted).
    const canonicalRegistry = (canonicalSlice.registry ?? {}) as Record<string, unknown>;
    const nextRegistry: Record<string, unknown> = { ...canonicalRegistry };
    const draftRefs = new Map<string, unknown>();
    for (const { id, draft } of drafts) {
      draftRefs.set(id, draft);
      if (draft[LOCAL_ENTITY_DELETE_MARKER] === true) {
        delete nextRegistry[id];
        continue;
      }
      // Neutral fusion on purpose: this runs for every drafted row on every store read, so it does
      // not pay for the draft normalization useEntityKernelState applies on its single-entity read.
      nextRegistry[id] = mergeEntityWithDraft(canonicalRegistry[id] ?? {}, draft);
    }

    const mergedSlice = { ...canonicalSlice, registry: nextRegistry };
    mergedSliceCache.set(canonicalSlice, { draftRefs, slice: mergedSlice });
    (merged as any)[type] = mergedSlice;
  }

  return merged;
};

// Whole-inventory memo keyed by (canonical inventory, entityKernelState): identical inputs return
// the same fused inventory, so repeated reads within one state snapshot never recompute.
const mergedInventoryByInputsCache = new WeakMap<
  KernelInventoryState,
  WeakMap<EntityKernelStateRegistry, KernelInventoryState>
>();

const getMergedInventorySnapshot = (state: KernelState): KernelInventoryState => {
  if (Object.keys(state.entityKernelState).length === 0) return state.inventory;

  const byEntityState = mergedInventoryByInputsCache.get(state.inventory);
  const cached = byEntityState?.get(state.entityKernelState);
  if (cached) return cached;

  const merged = withMergedInventory(state);
  const nextCache = byEntityState ?? new WeakMap<EntityKernelStateRegistry, KernelInventoryState>();
  if (!byEntityState) mergedInventoryByInputsCache.set(state.inventory, nextCache);
  nextCache.set(state.entityKernelState, merged);
  return merged;
};

// Appelé à la création du store, jamais à l'évaluation du module : lib/models.ts importe les
// panneaux de contrôle, qui importent ce fichier. Déréférencer modelDefaultParamGetters ici
// referme le cycle sur un const non encore initialisé (TDZ).
const buildInitialModelParams = () => Object.entries(modelDefaultParamGetters).reduce(
  (acc, [modelId, getter]) => {
    if (getter) acc[modelId] = getter();
    return acc;
  },
  {} as { [model: string]: DiffusionParams }
);

const initialUserPreferences: UserPreferences = {
  spectacleModeDefault: true,
  performanceHudEnabled: false,
  disabledCommands: [],
  lockSelectedModelForAgent: false,
  // Hidden until the real preference loads, so a user who already dismissed the loop hint
  // never sees it flash back on during the network round trip (see loadUserPreferences).
  shortcuts: defaultShortcuts,
  assistantModel: 'gemini-3.8-flash',
  modelVisibility: {},
  autoPromoteOnGenerate: true,
  imageVariationCount: 3,
  workspaceVersionPreviewFocusTopVisibleLayerRect: true,
  language: undefined,
  toastMode: 'error-only',
  activities: [...LEGACY_COMPAT_USER_ACTIVITIES],
  notificationPreferences: {
    dailyCreditUsageAlert: {
      enabled: false,
      thresholdCredits: 50,
      repeatStepCredits: 25,
      timezone: null,
    },
  },
};

const getInitialState = () => ({
  globalScrollTop: 0,
  diffusionModel: DEFAULT_STUDIO_MODEL_ID,
  spectacleMode: true,
  cloudAssetRegistry: [] as CloudAsset[],
  pendingAssetUrls: new Set<string>(),
  inflightLayerIds: new Set<string>(),
  isSubmittingAITask: false,
  isQueuingChatMessage: false,
  organisationCredits: null as CreditsData | null,
  isOrganisationCreditsLoading: true,
  organisationCreditsError: null as string | null,
  isOrganisationMember: false,
  activeModal: null as ModalName,
  modalPayload: null as any,
  menuData: null as QuickMenuData | null,
  chatPrompt: '',
  selectedAssetId: null as string | null,
  activeChatSessionId: null as string | null,
  imagesInChatInput: [] as LockableFile[],
  audioEnabled: false,
  // Web search is always on, the composer toggle was removed. Kept as a
  // pinned-true field so persisted state and the KernelState type stay valid.
  webSearchEnabled: true,
  internalDbEnabled: true,
  // Runtime errors observed this session, mounted to the agent every turn
  // until resolved/escalated (docs/specs/agent-error-pipeline.md).
  agentErrorLedger: [] as AgentObservedError[],
  agentModeOverride: undefined as AgentMode | undefined,
  modelParams: buildInitialModelParams(),
  saveTelemetry: null as SaveTelemetry | null,
  userPreferences: initialUserPreferences,
  userPreferencesOwnerId: null,
  actionHistory: [] as UserAction[],
  assistantTurnDebugLog: [] as AssistantTurnDebugInfo[],
  isDrawing: false,
  annotationMode: 'annotation' as const,
  isLoading: false,
  annotationBrushSize: 4,
  annotationColor: '#FF3B30',
  annotationBrushHardness: 0.85,
  inpaintMaskShapes: {} as Record<string, MaskShape[]>,
  masksHidden: false,
  maskPenMode: 'pen' as MaskPenMode,
  activeTool: null as InpaintTool,
  activeSoundTool: null as SoundTimelineTool,
  drawingToolKind: 'rectangle' as const,
  drawingStrokeColor: '#B3D9FF',
  drawingStrokeOpacity: 1,
  drawingFillColor: '#3B82F6',
  drawingFillOpacity: 1,
  drawingFillEnabled: false,
  drawingStrokeWidth: 4,
  drawingCornerRadius: 16,
  drawingFontSize: 48,
  drawingFontColor: '#FFFFFF',
  drawingFontOpacity: 1,
  drawingTextBold: false,
  drawingTextItalic: false,
  drawingTextUnderline: false,
  drawingFontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif',
  drawingFontWeight: 400,
  drawingLineHeight: 1.22,
  drawingLetterSpacingPercent: 0,
  drawingTextAlign: 'left' as const,
  layerToolColor: {r : 255, g : 255, b : 255, a : 1.0} as RgbaColor,
  layerToolWidth: 1,
  isLayerSliderVisible: false,
  themeOptions: { scanlineColor: '#B3D9FF', gridSpacing: 10, scanSpeed: 1.5, blueprintDashPattern: [4,4] } as ThemeOptions,
  isGuestMode: false,
  userContext: { dbUserId: null, organisationId: null, isImpersonating: false, impersonatedUser: null, trueUser: null, isFaterAdmin: false, isFaterOrgMember: false, isOrganisationAdmin: false } as UserContext,
  undoStack: [] as UndoableAction[],
  redoStack: [] as UndoableAction[],
  showResolutionHeatmap: false,
  nativeLayerCutSession: null,
  workspaceColumnPick: null,
  recastZonePick: null,
  isAltKeyPressed: false,
  isShiftKeyPressed: false,
  scissorsHoverLayerId: null as string | null,
  sourceFocusedFitId: null as string | null,
  // Dismissed rows are hidden by default: a shortlist is a list of candidates, and a run that
  // dismisses two thirds of what it scored would otherwise bury the ones worth reading. The toggle
  // exists because "where did my brands go" is the other failure - an extend that adds three and
  // dismisses two looks like it added one.
  sourceShowDismissed: false,
  sourceFocusedStoreId: null as string | null,
  sourceEnrichInFlight: 0,
  sourceEnrichingDims: {} as Record<string, string[]>,
  sourceBrandDetailTab: null as ('summary' | 'verdict' | 'scoring' | 'prices' | 'expansion' | 'stores' | 'gallery' | 'news' | 'revenue' | 'contacts' | null),
  sourceContactDiscoveryBrandId: null as string | null,
  // Raised while a rubric re-apply / doctrine re-judge is reworking a shortlist's scores, so the
  // table can read as being edited rather than merely loading. A counter, not a boolean: two
  // re-scores can overlap (the agent can fire one while the user fires another).
  sourceRescoreInFlight: 0,
  // Bulk-enrich pause/resume: the composer's Stop button raises the flag; the bulk loop
  // drains between brands and stashes the not-yet-enriched brandIds for a later resume.
  sourceEnrichStopRequested: false,
  sourceMandates: {} as Record<string, SourcingMandate>,
  sourceBudgetProposals: {},
  sourceEnrichPausedBrandIds: null as string[] | null,
  sourceSelectedFitIds: [] as string[],
  sourceHeaderTitle: null as string | null,
  sourceHeaderIsDashboard: false,
  sourceDashboardTab: 'shortlists' as const,
  daimonSessionId: null as string | null,
  pulse: { ownerKey: null as string | null, planId: null as string | null, propertyId: null as string | null, selectedUnitId: null as string | null, section: 'shops' as const, tool: 'select' as const, shortlist: null, draft: null, view3d: false, agentEdit: null, agentTouched: [], zoom: null, buildings: null, baseRevision: null as string | null, editVersion: 0, savedVersion: 0, saving: false, error: null as string | null, notice: null as string | null },
  sourceAssets: { ids: [] as string[], status: 'idle' as const },
  sourceOpeningRentRollId: null,
  sourceCreatingCampaign: false,
  sourceCampaignsRefreshToken: 0,
  /**
   * What the USER actually typed for the turn currently running, and nothing the agent produced.
   *
   * It exists so a command that SPENDS MONEY can check whether it was asked for, in code, instead
   * of trusting the model to have been asked. On 2026-08-08 the agent launched a 50-credit run in
   * a turn where it twice said it was not launching; doctrine was the only brake and it did not
   * hold.
   *
   * Rewritten by _executeDealerTurn at the start of EVERY turn, and non-empty only where askAssistant
   * declares that a person typed into the composer. So a recall, a goal loop pass, a setup-panel
   * reaction and the campaign-landing narration all read empty, which is exactly when a launch must
   * not be allowed to happen unasked. Do not derive it from `skipUserMessage`: that flag means the
   * message is already in the transcript, it is true on every askAssistant turn, and reading it here
   * left this field empty for real users for a week.
   */
  lastUserTurnPrompt: '' as string,
  lastUserChainPrompt: '' as string,
  // Doctrine is not inventory and has no socket stream: this token is what carries an agent's
  // instruction rewrite into the open Source right bar.
  sourceDoctrineRefreshToken: 0,
  /**
   * A new campaign is being prepared RIGHT NOW - the draft was asked for, the chat session it
   * needs is still being created.
   *
   * It exists to close a window, not to add a mode. `sourceCreatingCampaign` is deliberately
   * raised only AFTER the session swap (raising it first painted the previous conversation for a
   * beat), and during that round trip nothing is selected and no draft is open - which is exactly
   * the condition the view reads as "show the dashboard". So clicking New campaign flashed the
   * dashboard on the way in. This is true from the click, synchronously, and false once the draft
   * flag takes over.
   */
  portalStandaloneView: null,
  sourcePreparingCampaign: false,
  sourceOpeningCampaignId: null,
  studioOpeningProjectId: null,
  sourceDraftParams: null,
  sourcePendingParams: null,
  sourceSelectedCampaignId: null as string | null,
  // The dashboard lists every campaign; this narrows that one list to the runs that covered one
  // asset. null = every asset, which is also the whole portfolio.
  language: 'en' as 'en' | 'fr',
  pendingProjectSuccessMissionIds: new Set<string>(),
  missionsHydrated: false,
  missionsLoading: false,
  missionsError: null as string | null,
  debugActionInFlight: null as string | null,
  debugActionError: null as string | null,
  propertySearchQuery: '',
  dataConfidenceFilter: 'ALL' as 'ALL' | 'TRUSTED' | 'FATER',
  socketStatus: 'idle' as SocketStatus,
  socketReconnectAttempts: 0,
  socketLastEventTimestamp: null as string | null,
  activeRoleIds: [] as LeasingAgentRoleId[],
  entityKernelState: {} as EntityKernelStateRegistry,
  marketSignalStats: null as MarketSignalStats | null,
  leasingActions: [] as LeasingActionItem[], // Add this
  isLeasingActionsLoading: false,
  inventoryInitStatus: 'idle' as 'idle' | 'initializing' | 'initialized',
  inventory: {
    Property: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    CreditGrant: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Floor: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Unit: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Brand: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Portfolio: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    LegalEntity: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Deal: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    ChatSession: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    TimelineEvent: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    GeneratedDocument: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    BrandLocation: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    LegalEntityFinancialRecord: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    MarketSignal: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Audit: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    SlideCorrection: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Mission: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Campaign: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    SourcingCampaign: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    PortfolioFitCampaign: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    PortfolioFit: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Lead: { registry: {}, isLoading: false, error: null, lastActivity: 0, queryCache: {} },
    Project: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    Organisation: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    Team: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    User: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    Notification: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    Contact: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    UniversalFact: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    StudioWorkspace: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    StudioLayer: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
    StudioWorkspaceVersion: { isLoading: false, error: null, lastActivity: 0, registry: {}, queryCache: {} },
  },
  timelineFeedIndex: {
    bySessionId: {},
    bySubjectKey: {},
  } as TimelineFeedIndex,
  timelineFeedState: {} as TimelineFeedStateRegistry,
  inventoryCounts: {
    Property: {},
    Unit: {},
    Brand: {},
    Contact: {},
    Deal: {},
  } as InventoryCountState,
  inventoryStats: {
    Audit: {},
    Brand: {},
  } as InventoryStatsState,
  // Active generation semantics. This remains separate from the project-scoped
  // Canvas/List display selection; projectType is the source of this value.
  studioViewMode: 'studio' as StudioViewMode,
  studioSection: 'canvas' as StudioSection,
  factoryTour: null as KernelState['factoryTour'],
  modelistBudget: { unit: 'time', minutes: '60', credits: '300' } as ModelistBudgetChoice,
  fater3d: {
    view: 'dashboard', sitesLoaded: false, sites: [], filters: {}, sceneOf: null, sessionId: null, run: null, steps: {}, now: 0, error: null,
    generator: 'auto', views: {}, viewJobs: {}, place: null, movedPlace: null, projectLight: null, unconfirmed: {}, zoneNamed: false, landed: false, attachments: [], prepared: null, panelHost: null, light: LIGHT_DEFAULTS, render: RENDER_DEFAULTS,
  } as Fater3DState,
});

const generateSetters = (set: any, initialState: Record<string, any>): KernelSetters => {
  const setters: any = {};
  
  for (const key of Object.keys(initialState)) {
    const setterName = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
    setters[setterName] = (updater: any) => set((state: any) => ({
      [key]: typeof updater === 'function' ? updater(state[key]) : updater
    }));
  }
  return setters as KernelSetters;
};

// --- Factory Functions ---

export const createKernelStore = () => {
  return createStore<KernelState>((set, get) => {
    const initialState = getInitialState();
    const autoSetters = generateSetters(set, initialState);
    const setInventorySlice: KernelState['setInventorySlice'] = (entity, updater) => {
      set((state) => {
        const currentSlice = state.inventory[entity] as Parameters<typeof updater>[0];
        const nextSlice = updater(currentSlice);
        if (nextSlice === currentSlice) {
          return state;
        }
        return {
          inventory: {
            ...state.inventory,
            [entity]: nextSlice
          }
        };
      });
    };

    return {
      ...initialState,
      ...autoSetters,
      setInventorySlice,
    };
  });
};

// --- Contexts ---

export type KernelStoreApi = StoreApi<KernelState>;

export const KernelStoreContext = createContext<KernelStoreApi | null>(null);

// --- Global Singleton Instances (For Production Safety) ---

// Créé au premier rendu, pas à l'import : la construction de l'état initial lit lib/models.ts,
// qui importe les panneaux de contrôle, qui importent ce fichier. Instancier le store ici
// referme le cycle sur un module encore en cours d'évaluation (TDZ).
let globalKernelStoreInstance: KernelStoreApi | null = null;
const globalKernelStore = () => (globalKernelStoreInstance ??= createKernelStore());
// Per-render snapshot memos for useKernelStore: stabilize the fused state and each selector's
// result within a single state snapshot (the inventory read model itself lives at the top).
const mergedStateSnapshotCache = new WeakMap<KernelState, KernelState>();
const selectorResultSnapshotCache = new WeakMap<KernelState, Map<(state: KernelState) => unknown, unknown>>();

// --- Context-Aware Hooks ---

export const useKernelStore = <T>(selector: (state: KernelState) => T): T => {
  const contextStore = useContext(KernelStoreContext);
  const store = contextStore || globalKernelStore();
  return useStore(store, (state) => {
    const cachedMergedState = mergedStateSnapshotCache.get(state);
    const mergedState = cachedMergedState ?? (() => {
      const mergedInventory = getMergedInventorySnapshot(state);
      const nextMergedState =
        mergedInventory === state.inventory
          ? state
          : ({
              ...state,
              inventory: mergedInventory,
            } as KernelState);
      mergedStateSnapshotCache.set(state, nextMergedState);
      return nextMergedState;
    })();

    let selectorCacheForState = selectorResultSnapshotCache.get(mergedState);
    if (!selectorCacheForState) {
      selectorCacheForState = new Map<(state: KernelState) => unknown, unknown>();
      selectorResultSnapshotCache.set(mergedState, selectorCacheForState);
    }

    if (selectorCacheForState.has(selector)) {
      return selectorCacheForState.get(selector) as T;
    }

    const selected = selector(mergedState);
    selectorCacheForState.set(selector, selected);
    return selected;
  });
};

export const useKernelStoreApi = (): KernelStoreApi => {
  const contextStore = useContext(KernelStoreContext);
  return contextStore || globalKernelStore();
};
